import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import { Award, Users, Target, CheckCircle, Trash2, MapPin, Clock, Loader2, Globe } from 'lucide-react';
import { confirmDialog } from '../components/ui/confirm-dialog';
import notify from '../utils/notify';
import { myChallengeParticipation, myChallengeSubmission, challengeParticipantCount } from '../utils/communityMembership';

const TEAL = '#4DB6AC';

const LABELS = {
  en: { about: 'About', howTo: 'How to complete', location: 'Location', deadline: 'Deadline', impact: 'Your impact', joined: 'joined', frequency: 'Frequency', stars: 'stars', join: 'Join Challenge', logProgress: 'Log Progress', leave: 'Leave this action', delete: 'Delete this action' },
  hi: { about: '\u092A\u0930\u093F\u091A\u092F', howTo: '\u0915\u0948\u0938\u0947 \u092A\u0942\u0930\u093E \u0915\u0930\u0947\u0902', location: '\u0938\u094D\u0925\u093E\u0928', deadline: '\u0938\u092E\u092F \u0938\u0940\u092E\u093E', impact: '\u0906\u092A\u0915\u093E \u092A\u094D\u0930\u092D\u093E\u0935', joined: '\u0936\u093E\u092E\u093F\u0932', frequency: '\u0906\u0935\u0943\u0924\u094D\u0924\u093F', stars: '\u0938\u093F\u0924\u093E\u0930\u0947', join: '\u0936\u093E\u092E\u093F\u0932 \u0939\u094B\u0902', logProgress: 'Log Progress', leave: 'Leave this action', delete: 'Delete this action' },
};

export default function CommunityChallengeDetailPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAdmin, refreshUser } = useCommunityAuth();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logValue, setLogValue] = useState(1);
  const [logNote, setLogNote] = useState('');
  const [logging, setLogging] = useState(false);
  const [shareToFeed, setShareToFeed] = useState(false);
  const [shareText, setShareText] = useState('');
  const [shareImage, setShareImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Translation state (CHANGE 1)
  const [lang, setLang] = useState('en');
  const [translatedContent, setTranslatedContent] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const myProgress = myChallengeParticipation(challenge, user?.id);
  const isJoined = !!myProgress;

  // Frequency-based log status
  const getLogStatus = () => {
    const freq = challenge?.frequency;
    if (!freq || !isJoined) return { canLog: true, buttonText: 'Log Progress' };
    const logs = myProgress?.logs || [];
    const now = new Date();
    if (freq === 'one_time' && logs.length > 0)
      return { canLog: false, buttonText: 'Completed \u2713' };
    if (freq === 'daily') {
      const today = now.toISOString().slice(0, 10);
      if (logs.some(l => l.date?.slice(0, 10) === today))
        return { canLog: false, buttonText: 'Done for today' };
    }
    if (freq === 'weekdays') {
      const day = now.getDay();
      if (day === 0 || day === 6)
        return { canLog: false, buttonText: 'Available on weekdays only' };
      const today = now.toISOString().slice(0, 10);
      if (logs.some(l => l.date?.slice(0, 10) === today))
        return { canLog: false, buttonText: 'Done for today' };
    }
    if (freq === 'weekly') {
      const dayOfWeek = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
      const mondayStr = monday.toISOString().slice(0, 10);
      if (logs.some(l => (l.date?.slice(0, 10) || '') >= mondayStr))
        return { canLog: false, buttonText: 'Logged this week \u2713' };
    }
    return { canLog: true, buttonText: 'Log Progress' };
  };
  const logStatus = challenge ? getLogStatus() : { canLog: true, buttonText: 'Log Progress' };

  useEffect(() => { fetchChallenge(); }, [challengeId]);

  const fetchChallenge = async () => {
    try {
      const res = await axios.get(`${API}/community/challenges/${challengeId}`, authHeaders);
      setChallenge(res.data);
    } catch { navigate('/community'); }
    finally { setLoading(false); }
  };

  // CHANGE 2 — Grok API translation via backend proxy
  const handleToggleLang = async () => {
    if (lang === 'hi') { setLang('en'); return; }
    // Switching to Hindi
    const existingHi = challenge?.translations?.hi;
    if (existingHi && Object.keys(existingHi).length > 0) {
      setTranslatedContent(existingHi);
      setLang('hi');
      return;
    }
    if (translatedContent) { setLang('hi'); return; }
    // Call backend which calls Grok
    setIsTranslating(true);
    try {
      const res = await axios.post(`${API}/community/challenges/${challengeId}/translate`, {}, authHeaders);
      setTranslatedContent(res.data);
      setLang('hi');
    } catch {
      alert('Translation failed');
    } finally { setIsTranslating(false); }
  };

  // CHANGE 4 — Helper to get field value based on lang
  const L = LABELS[lang];
  const t = (enField, hiKey) => {
    if (lang === 'en' || !translatedContent) return enField;
    return translatedContent[hiKey] || enField;
  };
  const displayTitle = t(challenge?.title, 'title');
  const displayDesc = t(challenge?.description, 'description');
  const displayInstructions = (lang === 'hi' && translatedContent?.instructions?.length)
    ? translatedContent.instructions
    : (challenge?.instructions || []);
  const displayLocation = (lang === 'hi' && translatedContent?.location)
    ? translatedContent.location
    : [challenge?.venue_details, challenge?.city].filter(Boolean).join(', ');
  const displayType = t(challenge?.challenge_type, 'type');
  const displayFrequency = t(challenge?.frequency, 'frequencyVal');
  const displayDeadline = (lang === 'hi' && translatedContent?.deadline) ? translatedContent.deadline : null;
  const displayImpact = (lang === 'hi' && translatedContent?.impact) ? translatedContent.impact : null;

  const handleJoin = async () => {
    try {
      await axios.post(`${API}/community/challenges/${challengeId}/join`, {}, authHeaders);
      fetchChallenge();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleLeave = async () => {
    setShowLeaveConfirm(false);
    try {
      await axios.delete(`${API}/community/challenges/${challengeId}/leave`, authHeaders);
      // Refresh both the user (top-header stars) and the challenge (button
      // reverts to Join, progress card hides) so the UI reflects the new
      // state without forcing a page reload.
      if (typeof refreshUser === 'function') refreshUser();
      await fetchChallenge();
      notify.success('You have left this action.');
    } catch (err) {
      console.error('Failed to leave challenge:', err);
      notify.error(err?.response?.data?.detail || 'Failed to leave this action.');
    }
  };

  const handleLog = async () => {
    setLogging(true);
    try {
      let imageUrl = null;
      if (shareImage && shareToFeed) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', shareImage);
        const uploadRes = await axios.post(`${API}/community/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.url;
        setUploadingImage(false);
      }
      const body = {
        value: logValue, activity_type: challenge.challenge_type,
        note: logNote, share_to_feed: shareToFeed,
        share_text: shareText || `Logged progress on ${challenge.title}`,
        share_images: imageUrl ? [imageUrl] : [],
      };
      await axios.post(`${API}/community/challenges/${challengeId}/log`, body, authHeaders);
      setLogValue(1); setLogNote(''); setShareToFeed(false); setShareText(''); setShareImage(null);
      refreshUser();
      fetchChallenge();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setLogging(false); setUploadingImage(false); }
  };

  const handleDelete = async () => {
    if (!(await confirmDialog({ title: 'Delete this MAD Action?', message: 'This will permanently remove the action and its progress.', confirmText: 'Delete', danger: true }))) return;
    try {
      await axios.delete(`${API}/community/challenges/${challengeId}`, authHeaders);
      navigate('/community');
    } catch (err) { notify.error(err?.response?.data?.detail || 'Failed to delete action'); }
  };

  // Geo-attendance
  const mySub = myChallengeSubmission(challenge, user?.id);
  const geoRequired = challenge?.geo_tag_required;
  const startDate = challenge?.start_date ? new Date(challenge.start_date) : null;
  const isStarted = !startDate || new Date() >= startDate;

  const handleMarkAttendance = () => {
    setGeoLoading(true);
    setGeoError('');
    if (!navigator.geolocation) {
      setGeoError('Geolocation not supported by your browser');
      setGeoLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.post(`${API}/community/challenges/${challengeId}/submit-geo-attendance`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }, authHeaders);
          fetchChallenge();
        } catch (err) {
          setGeoError(err.response?.data?.detail || 'Submission failed');
        } finally { setGeoLoading(false); }
      },
      (err) => {
        setGeoError(err.code === 1 ? 'Location permission denied' : 'Could not get location');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  if (!challenge) return null;

  const progress = myProgress?.progress || 0;
  const pct = Math.min(100, (progress / challenge.target_value) * 100);

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA', paddingBottom: '80px' }}>
      <CommunityTopHeader showBack />

      <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: '16px 16px' }}>

        {/* Language Toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            data-testid="lang-toggle"
            onClick={handleToggleLang}
            disabled={isTranslating}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 20,
              border: `1.5px solid ${TEAL}`,
              background: lang === 'hi' ? TEAL : '#fff',
              color: lang === 'hi' ? '#fff' : TEAL,
              fontSize: 13, fontWeight: 600,
              cursor: isTranslating ? 'not-allowed' : 'pointer',
              opacity: isTranslating ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
          >
            {isTranslating ? (
              <><Loader2 size={14} className="animate-spin" /> Translating...</>
            ) : (
              <><Globe size={14} /> {lang === 'en' ? '\u0939\u093F\u0902\u0926\u0940' : 'EN'}</>
            )}
          </button>
        </div>

        {/* Header */}
        <div style={{ background: '#fff', borderRadius: 14, padding: 20, marginBottom: 12, border: '1px solid #F0F0F0' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', marginBottom: 6 }}>{displayTitle}</h2>
          <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>{displayDesc}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6B7280' }}>
              <Users size={14} /> {challengeParticipantCount(challenge)} {L.joined}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6B7280' }}>
              <Target size={14} /> Target: {challenge.target_value}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: TEAL }}>
              <Award size={14} /> +{challenge.reward_points} {L.stars}
            </span>
          </div>

          {/* Type badge */}
          {displayType && (
            <div style={{ marginTop: 10 }}>
              <span style={{
                display: 'inline-block', padding: '4px 10px', borderRadius: 12,
                background: `${TEAL}15`, color: TEAL, fontSize: 12, fontWeight: 600,
              }}>{displayType}</span>
            </div>
          )}

          {/* Event details if present */}
          {(challenge.start_date || displayLocation) && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#F9FAFB', borderRadius: 10, fontSize: 13, color: '#6B7280' }}>
              {challenge.start_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Clock size={13} />
                  <span style={{ fontWeight: 600, marginRight: 4 }}>{L.deadline}:</span>
                  {displayDeadline || (<>{new Date(challenge.start_date).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                  {challenge.end_date && <> &ndash; {new Date(challenge.end_date).toLocaleDateString(undefined, { dateStyle: 'medium' })}</>}</>)}
                </div>
              )}
              {displayLocation && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MapPin size={13} />
                  <span style={{ fontWeight: 600, marginRight: 4 }}>{L.location}:</span>
                  {displayLocation}
                </div>
              )}
            </div>
          )}

          {/* Frequency */}
          {challenge.frequency && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#6B7280' }}>
              <span style={{ fontWeight: 600 }}>{L.frequency}:</span> {displayFrequency}
            </div>
          )}

          {/* Impact */}
          {displayImpact && (
            <div style={{ marginTop: 8, fontSize: 13, color: TEAL, fontWeight: 600 }}>
              {L.impact}: {displayImpact}
            </div>
          )}
        </div>

        {/* Instructions */}
        {displayInstructions.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, border: '1px solid #F0F0F0' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 10 }}>{L.howTo}</h3>
            {displayInstructions.map((inst, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${TEAL}15`, color: TEAL,
                  fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{inst}</span>
              </div>
            ))}
          </div>
        )}

        {/* Geo-Attendance Block */}
        {geoRequired && isJoined && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, border: '1px solid #F0F0F0' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <MapPin size={15} color={TEAL} /> {L.location}
            </h3>
            {mySub ? (
              <div>
                {mySub.status === 'pending' && (
                  <div data-testid="geo-status-pending" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                    background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 20,
                    fontSize: 13, fontWeight: 600, color: '#92400E',
                  }}>
                    <Clock size={14} /> Submitted &ndash; Pending Review
                  </div>
                )}
                {mySub.status === 'approved' && (
                  <div data-testid="geo-status-approved" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                    background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 20,
                    fontSize: 13, fontWeight: 600, color: '#047857',
                  }}>
                    <CheckCircle size={14} /> Approved
                  </div>
                )}
                {mySub.status === 'rejected' && (
                  <div>
                    <div data-testid="geo-status-rejected" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                      background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 20,
                      fontSize: 13, fontWeight: 600, color: '#B91C1C', marginBottom: 6,
                    }}>
                      Rejected
                    </div>
                    {mySub.rejection_reason && (
                      <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>Reason: {mySub.rejection_reason}</div>
                    )}
                  </div>
                )}
              </div>
            ) : !isStarted ? (
              <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                Attendance opens on {startDate.toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </div>
            ) : (
              <div>
                <button data-testid="mark-attendance-btn" onClick={handleMarkAttendance} disabled={geoLoading}
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none',
                    background: geoLoading ? '#E5E7EB' : TEAL, color: geoLoading ? '#6B7280' : '#fff',
                    fontSize: 14, fontWeight: 600, cursor: geoLoading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: geoLoading ? 'none' : '0 4px 12px rgba(77,182,172,0.25)',
                  }}>
                  {geoLoading ? <><Loader2 size={16} className="animate-spin" /> Capturing location...</> : <><MapPin size={16} /> Mark Attendance</>}
                </button>
                {geoError && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{geoError}</div>}
              </div>
            )}
          </div>
        )}

        {/* Progress */}
        {isJoined && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, border: '1px solid #F0F0F0' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 10 }}>Your Progress</h3>
            <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: TEAL, borderRadius: 4, transition: 'width 0.5s' }} />
            </div>
            <div style={{ fontSize: 13, color: '#6B7280' }}>{progress} / {challenge.target_value} ({Math.round(pct)}%)</div>
          </div>
        )}

        {/* Log Progress */}
        {isJoined && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, border: '1px solid #F0F0F0' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 10 }}>{L.logProgress}</h3>
            {logStatus.canLog ? (<>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input type="number" min={1} value={logValue} data-testid="log-value"
                onChange={(e) => setLogValue(parseInt(e.target.value) || 1)}
                style={{ width: 80, padding: 10, borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 15, textAlign: 'center', outline: 'none' }} />
              <input placeholder="Note (optional)" value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 8 }}>
              <input type="checkbox" checked={shareToFeed} onChange={(e) => setShareToFeed(e.target.checked)} />
              Share to community feed
            </label>
            {shareToFeed && (
              <>
                <input placeholder="Share message" value={shareText}
                  onChange={(e) => setShareText(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', marginBottom: 10 }} />
                <label data-testid="share-image-input" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: '1.5px dashed #D1D5DB', cursor: 'pointer', marginBottom: 10, fontSize: 13, color: '#6B7280' }}>
                  <span>{shareImage ? shareImage.name : 'Attach photo (optional)'}</span>
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => setShareImage(e.target.files[0] || null)} />
                  {shareImage && (
                    <button onClick={(e) => { e.preventDefault(); setShareImage(null); }}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#EF4444', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                  )}
                </label>
              </>
            )}
            <button data-testid="submit-log" onClick={handleLog} disabled={logging}
              style={{ width: '100%', padding: '12px', background: logging ? '#9CA3AF' : TEAL, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: logging ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <CheckCircle size={16} /> {uploadingImage ? 'Uploading...' : logging ? 'Logging...' : L.logProgress}
            </button>
            </>) : (
              <button data-testid="submit-log" disabled
                style={{ width: '100%', padding: '12px', background: '#cccccc', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <CheckCircle size={16} /> {logStatus.buttonText}
              </button>
            )}
          </div>
        )}

        {/* Join / Leave / Delete — ended actions can no longer be joined */}
        {(() => {
          const isExpired = !!challenge?.end_date && new Date(challenge.end_date) < new Date();
          if (isJoined) {
            return (
              <button
                data-testid="leave-action-btn"
                onClick={() => setShowLeaveConfirm(true)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#FFFFFF',
                  color: '#DC2626',
                  border: '1.5px solid #FCA5A5',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}>
                Leave Action
              </button>
            );
          }
          if (isExpired) {
            return (
              <div data-testid="community-action-ended-label"
                style={{ width: '100%', padding: '14px', background: '#F1F5F9', color: '#64748B',
                  border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 14, fontWeight: 600,
                  textAlign: 'center' }}>
                This action has ended
              </div>
            );
          }
          return (
            <button data-testid="join-challenge" onClick={handleJoin}
              style={{ width: '100%', padding: '14px', background: TEAL, color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(77,182,172,0.25)' }}>
              {L.join}
            </button>
          );
        })()}

        {isAdmin && (
          <button data-testid="delete-challenge" onClick={handleDelete}
            style={{ width: '100%', padding: '10px', background: 'none', border: 'none',
              color: '#DC2626', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginTop: 4 }}>
            {L.delete}
          </button>
        )}
      </div>

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div
          data-testid="leave-confirm-modal"
          onClick={() => setShowLeaveConfirm(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF', borderRadius: 12, padding: 24,
              maxWidth: 340, width: '100%',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1F2937' }}>
              Leave this action?
            </h3>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20, lineHeight: 1.5 }}>
              Are you sure you want to leave this action? Your progress will be lost and your stars for this action will be deducted.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                data-testid="leave-cancel-btn"
                onClick={() => setShowLeaveConfirm(false)}
                style={{
                  flex: 1, padding: '12px', background: '#F9FAFB',
                  border: '1px solid #E5E7EB', borderRadius: 8,
                  fontWeight: 500, cursor: 'pointer', fontSize: 13, color: '#1F2937',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="leave-confirm-btn"
                onClick={handleLeave}
                style={{
                  flex: 1, padding: '12px', background: '#DC2626',
                  color: '#FFFFFF', border: 'none', borderRadius: 8,
                  fontWeight: 500, cursor: 'pointer', fontSize: 13,
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <CommunityBottomNav active="home" />
    </div>
  );
}
